/**
 * CodeEditor - Monaco Editor 기반 코드 편집기
 *
 * 기능:
 * - JSX/TSX 문법 하이라이팅
 * - 자동완성 (IntelliSense)
 * - 에러 표시
 * - VS Code 동일 경험
 */

import { useRef, useCallback, useEffect } from 'react';
import Editor, { type OnMount, type OnChange } from '@monaco-editor/react';
import type { editor } from 'monaco-editor';

export interface CodeEditorProps {
  /** 편집할 코드 */
  value: string;
  /** 코드 변경 핸들러 */
  onChange: (value: string) => void;
  /** 언어 (기본: tsx) */
  language?: 'typescript' | 'javascript' | 'html' | 'css';
  /** 읽기 전용 모드 */
  readOnly?: boolean;
  /** 에러 위치 표시 (라인 번호) */
  errorLine?: number;
  /** 에러 메시지 */
  errorMessage?: string;
  /** 테마 */
  theme?: 'vs-dark' | 'light';
  /** 높이 */
  height?: string;
  /** 클래스명 */
  className?: string;
}

export function CodeEditor({
  value,
  onChange,
  language = 'typescript',
  readOnly = false,
  errorLine,
  errorMessage,
  theme = 'vs-dark',
  height = '100%',
  className,
}: CodeEditorProps) {
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  const decorationsRef = useRef<string[]>([]);

  // 에디터 마운트 시 설정
  const handleEditorDidMount: OnMount = useCallback((editor, monaco) => {
    editorRef.current = editor;

    // JSX/TSX 설정
    monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
      target: monaco.languages.typescript.ScriptTarget.ESNext,
      allowNonTsExtensions: true,
      moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
      module: monaco.languages.typescript.ModuleKind.ESNext,
      noEmit: true,
      esModuleInterop: true,
      jsx: monaco.languages.typescript.JsxEmit.ReactJSX,
      reactNamespace: 'React',
      allowJs: true,
      typeRoots: ['node_modules/@types'],
    });

    // React 타입 정의 추가
    monaco.languages.typescript.typescriptDefaults.addExtraLib(
      `
      declare namespace React {
        function createElement(type: any, props?: any, ...children: any[]): any;
        function useState<T>(initial: T | (() => T)): [T, (value: T | ((prev: T) => T)) => void];
        function useEffect(effect: () => void | (() => void), deps?: any[]): void;
        function useRef<T>(initial: T): { current: T };
        function useCallback<T extends Function>(callback: T, deps: any[]): T;
        function useMemo<T>(factory: () => T, deps: any[]): T;
      }
      declare const React: typeof React;
      `,
      'file:///node_modules/@types/react/index.d.ts'
    );

    // 에디터 포커스
    editor.focus();
  }, []);

  // 코드 변경 핸들러
  const handleChange: OnChange = useCallback(
    (newValue) => {
      if (newValue !== undefined) {
        onChange(newValue);
      }
    },
    [onChange]
  );

  // 에러 표시 업데이트
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const monaco = (window as unknown as { monaco: typeof import('monaco-editor') }).monaco;
    if (!monaco) return;

    // 기존 데코레이션 제거
    decorationsRef.current = editor.deltaDecorations(decorationsRef.current, []);

    // 새 에러 표시
    if (errorLine && errorMessage) {
      decorationsRef.current = editor.deltaDecorations(
        [],
        [
          {
            range: new monaco.Range(errorLine, 1, errorLine, 1),
            options: {
              isWholeLine: true,
              className: 'error-line-decoration',
              glyphMarginClassName: 'error-glyph-margin',
              hoverMessage: { value: errorMessage },
            },
          },
        ]
      );
    }
  }, [errorLine, errorMessage]);

  return (
    <div className={className} style={{ height }}>
      <style>
        {`
          .error-line-decoration {
            background-color: rgba(255, 0, 0, 0.1);
          }
          .error-glyph-margin {
            background-color: #ff4444;
            border-radius: 50%;
            margin-left: 5px;
          }
        `}
      </style>
      <Editor
        height="100%"
        defaultLanguage={language}
        language={language}
        value={value}
        onChange={handleChange}
        onMount={handleEditorDidMount}
        theme={theme}
        options={{
          readOnly,
          minimap: { enabled: false },
          fontSize: 14,
          fontFamily: "'JetBrains Mono', 'Fira Code', Consolas, Monaco, monospace",
          lineNumbers: 'on',
          tabSize: 2,
          insertSpaces: true,
          automaticLayout: true,
          scrollBeyondLastLine: false,
          wordWrap: 'on',
          folding: true,
          bracketPairColorization: {
            enabled: true,
          },
          padding: {
            top: 16,
            bottom: 16,
          },
          suggest: {
            showKeywords: true,
            showSnippets: true,
          },
          quickSuggestions: true,
          formatOnPaste: true,
          formatOnType: true,
        }}
        loading={
          <div className="flex h-full items-center justify-center bg-gray-900 text-gray-400">
            에디터 로딩 중...
          </div>
        }
      />
    </div>
  );
}
